import { onMounted, onUnmounted, ref } from '@vue/composition-api';
import { $t } from '@/i18n';
import request from '@/utils/wujiFetch';
import { BASE_API_PATH } from '@/config/constant';
import { getLocationOrigin } from '@/utils/location';
import { useStore } from '@/store';
import { projectAPI } from './backendAPI';
import { fetchGogoBackendEnabled, getGogoBackendEnabled } from './useGogo';
const LOCAL_REMOTE_CONTAINER_WS_URL = typeof LOCAL_REMOTE_CONTAINER_URL !== 'undefined'
    ? LOCAL_REMOTE_CONTAINER_URL
    : '';
const DEVENV_TAR_URL_VALUE = typeof DEVENV_TAR_URL !== 'undefined' ? DEVENV_TAR_URL : '';
export const DEBUG_CONTAINER_TAG_VALUE = typeof DEBUG_CONTAINER_TAG !== 'undefined' ? DEBUG_CONTAINER_TAG : '';
// getTarUrl 接口地址（固定不变）：配了就在建容器前实时 GET 拿最新 COS tar url，
// 绕开构建期写死 DEVENV_TAR_URL（COS url 每次带 hash 变化）导致的指向旧包问题。
const GET_TAR_URL_ENDPOINT_VALUE = typeof GET_TAR_URL_ENDPOINT !== 'undefined' ? GET_TAR_URL_ENDPOINT : '';
/** 无头浏览器 AI 测试凭证（AES hex）：有值时走 /open/api/container/* + x-wuji-token 鉴权，绕过 OA */
const MANAGER_TOKEN_VALUE = typeof MANAGER_TOKEN !== 'undefined' ? MANAGER_TOKEN : '';
/**
 * AI 测试 playground 在真实浏览器里打开：
 * 构建期配了 MANAGER_TOKEN（无头鉴权），且当前页是 https。
 * 无头跑的是 http，走现有逻辑；浏览器 https 打开时要对齐 manager 协议，并把 wuji_host 强制 http 以便复用容器。
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const isAiCheckPlaygroundInBrowser = Boolean(MANAGER_TOKEN_VALUE)
    && window.location.protocol === 'https:';
function rewriteUrlProtocol(url, protocol) {
    if (!url)
        return url;
    try {
        const u = new URL(url);
        const secure = protocol === 'https:';
        if (u.protocol === 'ws:' || u.protocol === 'wss:') {
            u.protocol = secure ? 'wss:' : 'ws:';
        }
        else {
            u.protocol = protocol;
        }
        return u.toString().replace(/\/$/, '');
    }
    catch {
        return url;
    }
}
const configuredManagerUrl = typeof MANAGER_URL !== 'undefined' && MANAGER_URL
    ? MANAGER_URL.replace(/\/$/, '')
    : 'https://manager.wuji.woa.com';
export const MANAGER_BASE_URL = isAiCheckPlaygroundInBrowser
    ? rewriteUrlProtocol(configuredManagerUrl, 'https:')
    : configuredManagerUrl;
/** 建容器 / 查容器用的 wuji_host。playground 在浏览器 https 打开时强制 http，命中无头落库的记录。 */
export function toContainerWujiHost(host) {
    return isAiCheckPlaygroundInBrowser ? rewriteUrlProtocol(host, 'http:') : host;
}
/**
 * 与 /container/new 落库同一套 wuji_host：origin + pathPrefix，再走 toContainerWujiHost。
 * editor-remote-url / 版本查询必须用这个值，否则会命中其它站点的旧容器。
 */
export function resolveContainerWujiHost(override) {
    if (override)
        return toContainerWujiHost(override);
    const pathPrefix = (window.GLOBAL_INFO?.pathPrefix || '').replace(/\/$/, '');
    return toContainerWujiHost(getLocationOrigin() + pathPrefix);
}
/** 建容器 / editor-remote 共用的 wuji_host，先到先钉，避免两边各解析一次。 */
let pinnedCreateWujiHost = '';
function pinCreateWujiHost(host) {
    pinnedCreateWujiHost = host;
}
/**
 * 建容器 / editor-remote 查版本共用：getTarUrl 拿 devenv 包地址；
 * 若接口顺带给了 host 则覆盖落库 wuji_host。已 pin 则直接复用。
 */
export async function resolveCreateWujiHost(signal) {
    const latestBundle = GET_TAR_URL_ENDPOINT_VALUE
        ? await fetchLatestBundle(GET_TAR_URL_ENDPOINT_VALUE, signal)
        : { url: '', wujiHost: '' };
    const wujiHost = pinnedCreateWujiHost
        || (latestBundle.wujiHost
            ? resolveContainerWujiHost(latestBundle.wujiHost)
            : resolveContainerWujiHost());
    pinCreateWujiHost(wujiHost);
    return {
        wujiHost,
        devenvTarUrl: latestBundle.url || DEVENV_TAR_URL_VALUE,
    };
}
/** 容器宿主类型：cic | anyDev；构建期 CONTAINER_TYPE，默认 anyDev */
const CONTAINER_TYPE_VALUE = typeof CONTAINER_TYPE !== 'undefined' && CONTAINER_TYPE === 'cic'
    ? 'cic'
    : 'anyDev';
// 容器内业务工作目录 / 项目数据目录。显式下发，避免 manager 收到 undefined 后丢弃，
// 导致 devenv 端 workspace_dir / project_data_dir 回落到打包位置相关的兜底路径。
// 路径与容器宿主类型匹配：CIC 挂在 /data/cic/workspace，anyDev 挂在 /data/workspace。
const CONTAINER_WORKSPACE_DIR = CONTAINER_TYPE_VALUE === 'cic'
    ? '/data/cic/workspace/user_codes'
    : '/data/workspace/user_codes';
const CONTAINER_PROJECT_DATA_DIR = CONTAINER_TYPE_VALUE === 'cic'
    ? '/data/cic/workspace/project_data'
    : '/data/workspace/project_data';
/**
 * 从 getTarUrl 接口实时拿最新 devenv tar 下载地址。
 * 接口不可用 / 返回空 / 异常时返回空串，调用方回退构建期 DEVENV_TAR_URL。
 */
async function fetchLatestBundle(endpoint, signal) {
    if (!endpoint)
        return { url: '', wujiHost: '' };
    try {
        const res = await fetch(endpoint, { method: 'GET', signal, headers: { Accept: 'application/json' } });
        if (!res.ok)
            return { url: '', wujiHost: '' };
        const data = await res.json().catch(() => null);
        const url = data?.url;
        const wujiHost = data?.wujiHost;
        return {
            url: typeof url === 'string' ? url.trim() : '',
            wujiHost: typeof wujiHost === 'string' ? wujiHost.trim() : '',
        };
    }
    catch (error) {
        if (error?.name === 'AbortError')
            throw error;
        return { url: '', wujiHost: '' };
    }
}
function getContainerNewUrl() {
    return managerApiUrl('/container/new');
}
function getContainerReuseUrl() {
    return managerApiUrl('/container/reuse');
}
/**
 * playground / OA 用 query.reuseCid 分流：
 *   - 键不存在 → 普通 /container/new
 *   - 键在且非空 → 指定 cid reuse（仅 wuji）
 *   - 键在但为空（`?reuseCid` / `?reuseCid=` / `&reuseCid&www`）→ LRU scene=reuse
 */
function getReuseCidFromQuery() {
    const fromSearchParams = (raw) => {
        const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
        if (!params.has('reuseCid'))
            return null;
        return { present: true, cid: (params.get('reuseCid') || '').trim() };
    };
    const fromSearch = fromSearchParams(window.location.search);
    if (fromSearch)
        return fromSearch;
    const hash = window.location.hash || '';
    const hashQ = hash.indexOf('?');
    if (hashQ >= 0) {
        const fromHash = fromSearchParams(hash.slice(hashQ));
        if (fromHash)
            return fromHash;
    }
    // 裸键 `&reuseCid&` / `?reuseCid` 无等号：部分路由改写后 search 可能丢掉，href 上仍在。
    if (/[?&#]reuseCid(?:&|#|$)/.test(window.location.href)) {
        return { present: true, cid: '' };
    }
    return { present: false, cid: '' };
}
/**
 * 是否真的是 ai-code-check 的无头 Puppeteer 在跑：build 带 MANAGER_TOKEN 且页面被
 * ai-code-check 的 pageVisitor.ts 打上了 aiTestInPuppeteer 标记。同一个 build 被
 * 真人用浏览器打开时不会有这个 localStorage 标记，会自动落回 OA 路径（下面 /container/*），
 * 走访问者自己的太湖身份——避免所有真人共用 token 里写死的固定 rtx 导致复用容器混乱。
 */
const shouldUseManagerToken = Boolean(MANAGER_TOKEN_VALUE)
    && typeof window !== 'undefined'
    && window.localStorage?.getItem('aiTestInPuppeteer') === '1';
/** 确认是无头 Puppeteer 时走 /open/api，否则走业务侧 /container（真人走自己的 OA 身份） */
export function managerApiUrl(path) {
    const base = MANAGER_BASE_URL.replace(/\/$/, '');
    const prefix = shouldUseManagerToken ? '/open/api' : '';
    return `${base}${prefix}${path}`;
}
export function managerAuthHeaders() {
    return shouldUseManagerToken ? { 'x-wuji-token': MANAGER_TOKEN_VALUE } : {};
}
const MANAGER_HEALTH_MESSAGE_TYPE = 'wuji-gogo-manager-health';
class ManagerHttpError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.name = 'ManagerHttpError';
        this.status = status;
    }
}
function managerPublicUrl(path) {
    return `${MANAGER_BASE_URL.replace(/\/$/, '')}${path}`;
}
async function throwIfManagerRequestFailed(response) {
    if (response.ok)
        return;
    throw new ManagerHttpError(response.status, await parseErrorResponse(response));
}
/** 网关 401：带 X-Requested-With 探 /health，仍 401 则视为缺登录态。 */
async function isManagerLoginMissing(signal) {
    try {
        const res = await fetch(managerPublicUrl('/health'), {
            method: 'GET',
            credentials: 'include',
            signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        return res.status === 401;
    }
    catch (error) {
        if (error?.name === 'AbortError')
            throw error;
        return false;
    }
}
function openManagerLoginIframe(signal) {
    return new Promise((resolve) => {
        let settled = false;
        const wrapper = document.createElement('div');
        wrapper.style.cssText = [
            'position:fixed',
            'inset:0',
            'z-index:10000',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'background:rgba(0,0,0,0.5)',
        ].join(';');
        const iframe = document.createElement('iframe');
        iframe.src = managerPublicUrl('/health/html');
        iframe.style.cssText = [
            'width:min(880px,90vw)',
            'height:min(640px,85vh)',
            'border:none',
            'border-radius:8px',
            'background:#fff',
            'box-shadow:0 4px 12px rgba(0,0,0,0.15)',
        ].join(';');
        wrapper.appendChild(iframe);
        document.body.appendChild(wrapper);
        const finish = (result) => {
            if (settled)
                return;
            settled = true;
            window.removeEventListener('message', onMessage);
            signal?.removeEventListener('abort', onAbort);
            wrapper.remove();
            resolve(result);
        };
        const onMessage = (event) => {
            if (event.source !== iframe.contentWindow)
                return;
            if (event.data?.type === MANAGER_HEALTH_MESSAGE_TYPE)
                finish('logged-in');
        };
        const onAbort = () => finish('aborted');
        window.addEventListener('message', onMessage);
        signal?.addEventListener('abort', onAbort);
        wrapper.addEventListener('click', (e) => {
            if (e.target === wrapper)
                finish('aborted');
        });
        if (signal?.aborted)
            finish('aborted');
    });
}
async function recoverManagerLoginIfNeeded(error, signal) {
    if (!(error instanceof ManagerHttpError) || error.status !== 401)
        return false;
    if (!(await isManagerLoginMissing(signal)))
        return false;
    return (await openManagerLoginIframe(signal)) === 'logged-in';
}
/**
 * 把容器 service_url 与当前页协议对齐，再交给 editor-remote 转成 ws/wss。
 * - 页面 http（无头）：https → http，避免 connect 域 TLS 失败
 * - playground 在浏览器 https 打开：http → https，避免 mixed content（ws 会被拦，必须走 wss）
 */
function alignWithPageProtocol(url) {
    if (!url)
        return url;
    if (isAiCheckPlaygroundInBrowser)
        return rewriteUrlProtocol(url, 'https:');
    if (window.location.protocol === 'http:')
        return rewriteUrlProtocol(url, 'http:');
    return url;
}
async function parseErrorResponse(response) {
    const text = await response.text().catch(() => '');
    if (!text)
        return `${response.status} ${response.statusText}`;
    try {
        const data = JSON.parse(text);
        return data.error || data.msg || text;
    }
    catch (_e) {
        return text;
    }
}
async function readContainerNewSSE(response, onProgress) {
    if (!response.body) {
        throw new Error($t('k_9c3d1f8c'));
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const parseSSEBlock = (block) => {
        let event = 'message';
        const dataLines = [];
        block.split(/\r?\n/).forEach((line) => {
            if (line.startsWith('event:')) {
                event = line.slice('event:'.length).trim();
            }
            else if (line.startsWith('data:')) {
                dataLines.push(line.slice('data:'.length).trim());
            }
        });
        if (!dataLines.length)
            return null;
        const dataText = dataLines.join('\n');
        let data = dataText;
        try {
            data = JSON.parse(dataText);
        }
        catch (_e) {
            // 保留原始文本，后续按 message 兜底展示。
        }
        return { event, data };
    };
    const handleBlock = (block) => {
        const parsed = parseSSEBlock(block);
        if (!parsed)
            return null;
        const { event, data } = parsed;
        if (event === 'progress') {
            onProgress(data);
            return null;
        }
        if (event === 'done') {
            const serviceUrl = data?.container?.service_url;
            if (!serviceUrl) {
                throw new Error($t('k_2b0e5975'));
            }
            return data.container;
        }
        if (event === 'error') {
            throw new Error(data?.message || $t('k_3c073f1f'));
        }
        return null;
    };
    while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() || '';
        for (const block of blocks) {
            const result = handleBlock(block);
            if (result)
                return result;
        }
        if (done) {
            if (buffer.trim()) {
                const result = handleBlock(buffer);
                if (result)
                    return result;
            }
            break;
        }
    }
    throw new Error($t('k_df849638'));
}
async function createContainer(payload, signal, onProgress, extraHeaders = {}) {
    const response = await fetch(getContainerNewUrl(), {
        method: 'POST',
        credentials: 'include',
        signal,
        headers: {
            Accept: 'text/event-stream',
            'Content-Type': 'application/json',
            ...managerAuthHeaders(),
            ...extraHeaders,
        },
        body: JSON.stringify(payload),
    });
    await throwIfManagerRequestFailed(response);
    return readContainerNewSSE(response, onProgress);
}
async function reuseRemoteContainer(payload, signal, onProgress) {
    onProgress({ stage: 'reusing', message: $t('k_613dc9a9') });
    const response = await fetch(getContainerReuseUrl(), {
        method: 'POST',
        credentials: 'include',
        signal,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...managerAuthHeaders(),
        },
        body: JSON.stringify(payload),
    });
    await throwIfManagerRequestFailed(response);
    const data = await response.json().catch(() => null);
    const container = data?.container;
    if (!container?.service_url) {
        throw new Error($t('k_4b741401'));
    }
    onProgress({ stage: 'ready', message: $t('k_eb25ef7') });
    return container;
}
/**
 * 这个 AI 测试专属构建对应的 /projects/server 部署配了 debug.rtx（整条 OA 链路被禁用，
 * ctx.rtx 恒为固定的调试用户），本地 `${BASE_API_PATH}/privatekey` 拿到的永远是那个固定
 * 用户的 secretKey。真人在浏览器里打开时，要先问 manager（走真实太湖身份）拿到真实 rtx，
 * 再让 server 按这个 rtx 查/建 key ——避免 user_id=真人 + private_key=固定调试用户的组合，
 * 导致 devenv 端对平台鉴权时 "Decryption failed" (rtx 与密钥持有者不匹配)。
 * 无头 Puppeteer（shouldUseManagerToken=true）不受影响，继续走原逻辑（固定调试身份自洽）。
 */
async function fetchManagerRealRtx() {
    // 注意：用业务侧 /container/whoami（仅 taihu，对所有登录用户开放），
    // 不能用 /manager/whoami——那条路由多挂了 scope=wuji，普通业务用户会 403。
    const response = await fetch(`${MANAGER_BASE_URL.replace(/\/$/, '')}/container/whoami`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok)
        throw new ManagerHttpError(response.status, `container/whoami http ${response.status}`);
    const data = await response.json().catch(() => null);
    const rtx = data?.rtx;
    if (!rtx || typeof rtx !== 'string')
        throw new Error($t('k_9a4110ec'));
    return rtx;
}
export async function getCurrentUserPrivateKey() {
    if (Boolean(MANAGER_TOKEN_VALUE) && !shouldUseManagerToken) {
        const rtx = await fetchManagerRealRtx();
        const { secretKey } = await request(`${BASE_API_PATH}/debug-only/privatekey?rtx=${encodeURIComponent(rtx)}`) || {};
        return secretKey || '';
    }
    const { secretKey: existingKey } = await request(`${BASE_API_PATH}/privatekey`) || {};
    if (existingKey)
        return existingKey;
    const { secretKey: newKey } = await request(`${BASE_API_PATH}/privatekey`, {
        method: 'POST',
    }) || {};
    return newKey || '';
}
export async function openGogoRemoteContainer(options) {
    if (LOCAL_REMOTE_CONTAINER_WS_URL) {
        return {
            wsUrl: LOCAL_REMOTE_CONTAINER_WS_URL,
            webIdeUrl: '',
            isAnyDev: false,
            projectFramework: 'vue',
        };
    }
    const store = useStore();
    const pathPrefix = (window.GLOBAL_INFO?.pathPrefix || '').replace(/\/$/, '');
    let wujiHost = window.location.origin + pathPrefix;
    const [projectInfo] = await Promise.all([
        projectAPI.getProjectInfo(options.projectId),
        fetchGogoBackendEnabled(),
    ]);
    const projectFramework = projectInfo?.artifact?.meta?.framework === 'react' ? 'react' : 'vue';
    const userId = window.w?.user?.userId;
    const latestBundle = GET_TAR_URL_ENDPOINT_VALUE
        ? await fetchLatestBundle(GET_TAR_URL_ENDPOINT_VALUE, options.signal)
        : { url: '', wujiHost: '' };
    const devenvTarUrl = latestBundle.url || DEVENV_TAR_URL_VALUE;
    if (GET_TAR_URL_ENDPOINT_VALUE && !devenvTarUrl) {
        const message = $t('k_d7e675e1');
        throw new Error(message);
    }
    if (latestBundle.wujiHost)
        wujiHost = latestBundle.wujiHost;
    wujiHost = toContainerWujiHost(wujiHost);
    const platformProjectName = store.state.project.projectInfo?.name;
    while (true) {
        try {
            const privateKey = await getCurrentUserPrivateKey();
            const payload = {
                projectId: options.projectId,
                branchId: options.branchId || undefined,
                displayName: platformProjectName ? $t('k_1a737576', { arg_0: platformProjectName }) : undefined,
                userId: userId || undefined,
                wuji_host: wujiHost,
                workspace_dir: CONTAINER_WORKSPACE_DIR,
                project_data_dir: CONTAINER_PROJECT_DATA_DIR,
                promptType: projectFramework === 'react' ? 'react' : 'vue3',
                canDevelopBackend: getGogoBackendEnabled(),
                private_key: privateKey,
                container_type: CONTAINER_TYPE_VALUE,
                ...(devenvTarUrl ? { devenv_tar_url: devenvTarUrl } : {}),
                ...(DEBUG_CONTAINER_TAG_VALUE ? { debug_tag: DEBUG_CONTAINER_TAG_VALUE } : {}),
            };
            const container = await createContainer(payload, options.signal, options.onProgress ?? (() => { }));
            return {
                wsUrl: alignWithPageProtocol(container.service_url || ''),
                webIdeUrl: container.web_ide_url || '',
                isAnyDev: container.container_type === 'anyDev',
                projectFramework,
            };
        }
        catch (error) {
            if (error?.name === 'AbortError')
                throw error;
            if (await recoverManagerLoginIfNeeded(error, options.signal))
                continue;
            throw error;
        }
    }
}
export function useGogoRemoteContainer(options) {
    const store = useStore();
    const wsUrl = ref('');
    const webIdeUrl = ref('');
    const wujiHost = ref('');
    const isAnyDev = ref(false);
    const containerReusable = ref(false);
    const containerReady = ref(false);
    const containerLoading = ref(true);
    const containerError = ref('');
    const containerProgressMessage = ref($t('k_694e6bc4'));
    const containerErrorTitle = $t('k_fa06eb3d');
    const containerLoadingTitle = ref($t('k_d129778e'));
    const retryText = $t('k_a815dd36');
    const projectFramework = ref('vue');
    let abortController = null;
    const updateContainerProgress = (data) => {
        const stage = data?.stage;
        // 唤醒阶段：更新标题为"正在唤醒容器"
        if (stage === 'waking' || data?.waking) {
            containerLoadingTitle.value = $t('k_188d3dd7');
        }
        if (data?.message) {
            containerProgressMessage.value = data.message;
            return;
        }
        if (stage) {
            containerProgressMessage.value = $t('k_cc73995d', { arg_0: stage });
        }
    };
    async function startCreateContainer() {
        abortController?.abort();
        abortController = new AbortController();
        containerLoading.value = true;
        containerReady.value = false;
        containerReusable.value = false;
        containerError.value = '';
        containerProgressMessage.value = $t('k_694e6bc4');
        containerLoadingTitle.value = $t('k_d129778e');
        if (LOCAL_REMOTE_CONTAINER_WS_URL) {
            const host = resolveContainerWujiHost();
            wujiHost.value = host;
            pinCreateWujiHost(host);
            wsUrl.value = LOCAL_REMOTE_CONTAINER_WS_URL;
            containerReady.value = true;
            containerLoading.value = false;
            return;
        }
        try {
            while (true) {
                try {
                    const branchId = options.getBranchId?.() || '';
                    // 复用逻辑已下沉到 manager /container/new：命中 (用户名 + 分支 + 项目 + wuji_host)
                    // 的健康容器会直接以 done 事件返回并携带 service_url，无需前端再单独查 /container/get。
                    // getProjectInfo 内部按 getProjectId()（route query.projectid）取当前项目，不接受
                    // 传入 projectId（历史遗留参数从未被使用），这里之前传 options.projectId 是误用。
                    const [projectInfo, , createHost] = await Promise.all([
                        projectAPI.getProjectInfo(),
                        fetchGogoBackendEnabled(),
                        resolveCreateWujiHost(abortController.signal),
                    ]);
                    projectFramework.value = projectInfo?.artifact?.meta?.framework || 'vue';
                    const userId = window.w?.user?.userId;
                    const privateKey = await getCurrentUserPrivateKey();
                    const { wujiHost: resolvedWujiHost, devenvTarUrl } = createHost;
                    wujiHost.value = resolvedWujiHost;
                    pinCreateWujiHost(resolvedWujiHost);
                    // AI ProjectInfo 没有 name；平台项目展示名在 store.state.project.projectInfo.name。
                    // 以前误读 (projectInfo as any).name → 恒为空 → 群名回落成「项目id（rtx）」。
                    const platformProjectName = store.state.project.projectInfo?.name;
                    const reuseQuery = getReuseCidFromQuery();
                    // query.reuseCid，或无头/open 通道有 MANAGER_TOKEN：都走 reuse（空 cid = LRU）。
                    const shouldSendReuseCid = reuseQuery.present || Boolean(MANAGER_TOKEN_VALUE);
                    const payload = {
                        projectId: options.projectId,
                        branchId: branchId || undefined,
                        displayName: platformProjectName ? `[无极]${platformProjectName}` : undefined,
                        userId: userId || undefined,
                        wuji_host: resolvedWujiHost,
                        workspace_dir: CONTAINER_WORKSPACE_DIR,
                        project_data_dir: CONTAINER_PROJECT_DATA_DIR,
                        promptType: projectFramework.value === 'react' ? 'react' : 'vue3',
                        canDevelopBackend: getGogoBackendEnabled(),
                        private_key: privateKey,
                        container_type: CONTAINER_TYPE_VALUE,
                        ...(devenvTarUrl ? { devenv_tar_url: devenvTarUrl } : {}),
                        // reuse 在时不要带 debug_tag，否则 manager 会忽略 reuse 走普通机。
                        ...(!shouldSendReuseCid && DEBUG_CONTAINER_TAG_VALUE ? { debug_tag: DEBUG_CONTAINER_TAG_VALUE } : {}),
                    };
                    let container;
                    if (reuseQuery.present && reuseQuery.cid) {
                        container = await reuseRemoteContainer({ ...payload, cid: reuseQuery.cid }, abortController.signal, updateContainerProgress);
                    }
                    else {
                        container = await createContainer(payload, abortController.signal, updateContainerProgress, 
                        // fetch/Chromium 会丢掉值为 "" 的 header，LRU 时必须传非空占位才进请求。
                        // /container/new 只根据 x-reuse-cid 是否出现走 LRU；具体 cid 走 /container/reuse。
                        shouldSendReuseCid ? { 'x-reuse-cid': reuseQuery.cid || 'auto' } : {});
                    }
                    wsUrl.value = alignWithPageProtocol(container.service_url || '');
                    webIdeUrl.value = container.web_ide_url || '';
                    isAnyDev.value = container.container_type === 'anyDev';
                    containerReusable.value = container.scene === 'reuse';
                    containerReady.value = true;
                    break;
                }
                catch (error) {
                    if (error?.name === 'AbortError')
                        return;
                    if (await recoverManagerLoginIfNeeded(error, abortController.signal))
                        continue;
                    throw error;
                }
            }
        }
        catch (error) {
            if (error?.name === 'AbortError')
                return;
            console.error('[GogoEditorRemote] create container failed', error);
            containerError.value = error?.message || $t('k_3c073f1f');
        }
        finally {
            containerLoading.value = false;
        }
    }
    function retryCreateContainer() {
        void startCreateContainer();
    }
    onMounted(() => {
        void startCreateContainer();
    });
    onUnmounted(() => {
        abortController?.abort();
        abortController = null;
    });
    return {
        wsUrl,
        webIdeUrl,
        wujiHost,
        isAnyDev,
        containerReusable,
        containerReady,
        containerLoading,
        containerError,
        containerProgressMessage,
        containerErrorTitle,
        containerLoadingTitle,
        retryText,
        retryCreateContainer,
    };
}
